/* global braze */
import Mustache from 'mustache';

const DEFAULT_PRIORITY = 1;
const CTA_CLICK_EVENT_NAME = 'FirstPartyAdClicked';
const BRAZE_SAFE_TAG_OPEN = '<%';
const BRAZE_SAFE_TAG_CLOSE = '%>';
const MUSTACHE_TAG_OPEN = '{{';
const MUSTACHE_TAG_CLOSE = '}}';
const AD_VIEWED_EVENT_NAME = 'FirstPartyAdViewed';
const AD_VIEWED_THRESHOLD = 0.5;
const PITCH_IN_VIEW_DATALAYER_EVENT_NAME = 'pitch_in_view'; // legacy GTM event
const PITCH_PLACEMENT = 'pitch';
const AD_CONTAINER_SELECTOR = '.ad-container';
const CONTAINER_VARIABLE_PATTERN = /\|([a-zA-Z][a-zA-Z0-9_]*)\|/g;
const ELEMENT_NODE = 1;
const TEXT_NODE = 3;
const AD_LINK_ATTRIBUTES = { target: '_blank', rel: 'noopener noreferrer' };
const CONTENT_CARDS_MAX_AGE_MS = 30 * 60 * 1000;

function cardPriority(contentCard) {
  const priority = parseInt(contentCard.extras.priority, 10);

  return Number.isNaN(priority) ? DEFAULT_PRIORITY : priority;
}

function groupCardsByAdType(contentCards) {
  return contentCards.reduce((groups, contentCard) => {
    const adType = contentCard.extras.ad_type;
    (groups[adType] ??= []).push(contentCard);

    return groups;
  }, {});
}

function electCardForAdType(adType, contentCards) {
  const elected = contentCards.reduce((winner, contentCard) =>
    cardPriority(contentCard) > cardPriority(winner) ? contentCard : winner
  );

  const discardedTies = contentCards.filter(
    (contentCard) =>
      contentCard !== elected &&
      cardPriority(contentCard) === cardPriority(elected)
  );

  if (discardedTies.length > 0) {
    const discardedTitles = discardedTies
      .map((contentCard) => contentCard.title)
      .join(', ');

    console.warn(
      `BrazeContentCardAds.electCardForAdType -- Braze Ad ${elected.title} took the ${adType} placement at priority ${cardPriority(elected)}, discarding ${discardedTitles} at the same priority. Give competing campaigns distinct priorities in Braze.`
    );
  }

  return elected;
}

function toMustacheTags(template) {
  return template
    .replaceAll(BRAZE_SAFE_TAG_OPEN, MUSTACHE_TAG_OPEN)
    .replaceAll(BRAZE_SAFE_TAG_CLOSE, MUSTACHE_TAG_CLOSE);
}

function renderCardDescription(contentCard) {
  const template = (contentCard.description ?? '').trim();

  if (template === '') {
    return '';
  }

  try {
    return Mustache.render(
      toMustacheTags(template),
      window.FOOL_BRAZE_ADS.ADS_CONTEXT
    ).trim();
  } catch (error) {
    console.error(
      `BrazeContentCardAds.renderCardDescription -- Braze Ad ${contentCard.title} has a template that could not be parsed: ${error.message}`
    );

    return '';
  }
}

// allow defining vars to prevent Mustache tag repetition.
function resolveContainerVariables(text, element) {
  return text.replace(CONTAINER_VARIABLE_PATTERN, (token, variableName) => {
    const container = element.closest(AD_CONTAINER_SELECTOR);
    const value = container?.dataset?.[variableName];

    if (value === undefined) {
      console.warn(
        `BrazeContentCardAds.resolveContainerVariables -- ${token} has no matching data attribute on the ad container.`
      );

      return '';
    }

    return value;
  });
}

function applyLinkAttributes(element) {
  for (const link of element.querySelectorAll('a')) {
    for (const [name, value] of Object.entries(AD_LINK_ATTRIBUTES)) {
      if (!link.hasAttribute(name)) {
        link.setAttribute(name, value);
      }
    }
  }
}

function resolveContainerVariablesIn(element) {
  for (const node of [...element.childNodes]) {
    if (node.nodeType === TEXT_NODE) {
      node.nodeValue = resolveContainerVariables(node.nodeValue, element);
      continue;
    }

    if (node.nodeType !== ELEMENT_NODE) {
      continue;
    }

    for (const { name, value } of [...node.attributes]) {
      const resolved = resolveContainerVariables(value, node);

      if (resolved !== value) {
        node.setAttribute(name, resolved);
      }
    }

    resolveContainerVariablesIn(node);
  }
}

class BrazeContentCardAds {
  constructor() {
    this.renderedAdTypes = new Set();
    this.trackedAdTypes = new Set();
    this.viewedAds = new WeakMap();
    this.viewObserver = new IntersectionObserver(
      (entries, observer) => this.reportAdViews(entries, observer),
      { threshold: AD_VIEWED_THRESHOLD }
    );
  }

  reportAdViews(entries, observer) {
    for (const entry of entries) {
      if (!entry.isIntersecting) {
        continue;
      }

      observer.unobserve(entry.target);

      const viewedAd = this.viewedAds.get(entry.target);
      if (!viewedAd) {
        continue;
      }

      window.foolTrackEvent?.(AD_VIEWED_EVENT_NAME, viewedAd.adProperties);

      if (!viewedAd.adType.toLowerCase().includes(PITCH_PLACEMENT)) {
        continue;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: PITCH_IN_VIEW_DATALAYER_EVENT_NAME,
        pitch_type: viewedAd.adType,
      });
      console.log('Pitch in view:', viewedAd.adType);
    }
  }

  observeAdView(adType, adElement, adProperties) {
    if (!adType) {
      return;
    }

    this.viewedAds.set(adElement, { adType, adProperties });
    this.viewObserver.observe(adElement);
  }

  trackCtaClicks(adType, adElement, adProperties) {
    if (!adType || this.trackedAdTypes.has(adType)) {
      return;
    }

    this.trackedAdTypes.add(adType);

    adElement.addEventListener('click', (event) => {
      const cta = event.target.closest('a');

      if (!cta || !adElement.contains(cta)) {
        return;
      }

      window.foolTrackEvent?.(CTA_CLICK_EVENT_NAME, {
        ...adProperties,
        destination: cta.href,
      });
    });
  }

  trackDefaultAdPlacements() {
    const filledPlacements = document.querySelectorAll(
      '.braze-content-card[data-braze-activated]'
    );

    for (const adElement of filledPlacements) {
      const adType = adElement.dataset.adType;

      if (this.renderedAdTypes.has(adType)) {
        continue;
      }

      const adProperties = { ad_type: adType, is_default_ad: 'true' };

      this.trackCtaClicks(adType, adElement, adProperties);
      this.observeAdView(adType, adElement, adProperties);
    }
  }

  doesCurrentUrlMatchPattern(pattern) {
    return new RegExp(pattern).test(location.pathname.substring(1));
  }

  isCardEligible(contentCard) {
    if (contentCard.extras.tier !== window.FOOL_BRAZE_ADS.ADS_CONTEXT.tier) {
      console.log(
        `BrazeContentCardAds.isCardEligible --- skipped Braze Ad ${contentCard.title} due to non-matching tier.`
      );

      return false;
    }

    if (
      'url_pattern' in contentCard.extras &&
      !this.doesCurrentUrlMatchPattern(contentCard.extras.url_pattern)
    ) {
      console.log(
        `BrazeContentCardAds.isCardEligible --- skipped Braze Ad ${contentCard.title} due to non-matching URL pattern.`
      );

      return false;
    }

    return true;
  }

  renderAd(adType, contentCard, cardContent) {
    const adElement = document.querySelector(
      `.braze-content-card[data-ad-type="${adType}"]`
    );
    if (!adElement) {
      console.warn(
        `BrazeContentCardAds.renderAd -- Braze Ad ${contentCard.title} is missing the ${adType} placeholder element.`
      );

      return;
    }

    this.replacePlacementWithCardContent(
      adType,
      adElement,
      contentCard,
      cardContent
    );

    const adProperties = {
      ...contentCard.extras,
      title: contentCard.title,
      is_default_ad: 'false',
    };

    this.trackCtaClicks(adType, adElement, adProperties);
    this.observeAdView(adType, adElement, adProperties);
  }

  replacePlacementWithCardContent(adType, adElement, contentCard, cardContent) {
    const adContent = document.createElement('div');
    adContent.className = 'braze-ad';
    adContent.dataset.adTitle = contentCard.title;
    adContent.innerHTML = cardContent;

    // add the placement as a container class for styling, e.g. `.pitch`
    for (const container of adContent.querySelectorAll(AD_CONTAINER_SELECTOR)) {
      container.classList.add(adType);
    }

    resolveContainerVariablesIn(adContent);
    applyLinkAttributes(adContent);

    adElement.innerHTML = '';
    adElement.appendChild(adContent);
    adElement.dataset.brazeActivated = '1';
    this.renderedAdTypes.add(adType);
  }

  processContentCards(contentCards) {
    const cardContents = new Map();
    const candidates = [];

    for (const contentCard of contentCards) {
      if (!this.isCardEligible(contentCard)) {
        continue;
      }

      const cardContent = renderCardDescription(contentCard);

      if (cardContent === '') {
        console.log(
          `BrazeContentCardAds.processContentCards --- skipped Braze Ad ${contentCard.title} because it has no creative for this page.`
        );

        continue;
      }

      cardContents.set(contentCard, cardContent);
      candidates.push(contentCard);
    }

    const cardsByAdType = groupCardsByAdType(candidates);

    for (const [adType, contestants] of Object.entries(cardsByAdType)) {
      if (this.renderedAdTypes.has(adType)) {
        continue;
      }

      const elected = electCardForAdType(adType, contestants);

      this.renderAd(adType, elected, cardContents.get(elected));
    }
  }

  subscribeToContentCardUpdates() {
    braze.subscribeToContentCardsUpdates((contentCards) => {
      this.processContentCards(contentCards.cards);
    });
  }

  areCachedContentCardsStale() {
    const { lastUpdated } = braze.getCachedContentCards();

    return (
      !lastUpdated ||
      Date.now() - lastUpdated.getTime() > CONTENT_CARDS_MAX_AGE_MS
    );
  }

  processCachedContentCards() {
    this.processContentCards(braze.getCachedContentCards().cards ?? []);
  }

  async handleDefaultBrazeAds() {
    if ('foolDefaultBrazeAdsHandler' in window) {
      await window.foolDefaultBrazeAdsHandler();
    }

    this.trackDefaultAdPlacements();
  }

  async renderAdsOnPageLoad() {
    this.processCachedContentCards();

    if (this.renderedAdTypes.size === 0 || this.areCachedContentCardsStale()) {
      braze.requestContentCardsRefresh(async () => {
        this.processCachedContentCards();
        await this.handleDefaultBrazeAds();
      });

      return;
    }

    await this.handleDefaultBrazeAds();
  }

  start() {
    this.subscribeToContentCardUpdates();
    window.addEventListener('load', () => this.renderAdsOnPageLoad());
  }
}

// Dropped from the bundled IIFE that ships; present so the unit tests can reach the internals.
export {
  cardPriority,
  groupCardsByAdType,
  electCardForAdType,
  renderCardDescription,
  toMustacheTags,
  resolveContainerVariablesIn,
  applyLinkAttributes,
  BrazeContentCardAds,
};

document.addEventListener('fool_braze_initialized', () => {
  if (!Object.prototype.hasOwnProperty.call(window, 'FOOL_BRAZE_ADS')) {
    return;
  }

  new BrazeContentCardAds().start();
});
